import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NDA_STORAGE_KEY } from "@/lib/constants";

const NDA_TEXT = `MUTUAL NON-DISCLOSURE AGREEMENT

This Non-Disclosure Agreement ("Agreement") is entered into by and between COSINE X LTD ("Disclosing Party") and the undersigned ("Receiving Party").

1. CONFIDENTIAL INFORMATION. "Confidential Information" means any non-public information disclosed by the Disclosing Party, including but not limited to: business plans, financial data, technical specifications, product roadmaps, customer data, and any materials marked as confidential.

2. OBLIGATIONS. The Receiving Party agrees to: (a) hold all Confidential Information in strict confidence; (b) not disclose Confidential Information to any third party without prior written consent; (c) use Confidential Information solely for the purpose of evaluating a potential business relationship.

3. TERM. This Agreement shall remain in effect for two (2) years from the date of execution.

4. RETURN OF MATERIALS. Upon request, the Receiving Party shall promptly return or destroy all Confidential Information and any copies thereof.

5. GOVERNING LAW. This Agreement shall be governed by the laws of England and Wales.`;

export function NDAForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { from?: string })?.from || "/ir/dataroom/docs";

  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    title: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [error, setError] = useState("");

  const handleChange = useCallback(
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setError("");
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!form.name || !form.email || !form.company) {
        setError("Please fill in all required fields.");
        return;
      }
      if (!agreed) {
        setError("Please agree to the NDA terms.");
        return;
      }
      if (!signatureName) {
        setError("Please type your name as electronic signature.");
        return;
      }

      // Phase 1: localStorage gate
      localStorage.setItem(NDA_STORAGE_KEY, "1");
      navigate(returnTo, { replace: true });
    },
    [form, agreed, signatureName, navigate, returnTo]
  );

  const inputClass =
    "w-full px-4 py-2.5 rounded-lg border border-[var(--color-border)] bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-[13px] outline-none focus:border-[var(--color-accent)] transition-colors";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-2xl mx-auto">
      {/* Contact fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
            Full Name *
          </label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange("name")}
            className={inputClass}
            placeholder="Jane Smith"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
            Email *
          </label>
          <input
            type="email"
            value={form.email}
            onChange={handleChange("email")}
            className={inputClass}
            placeholder="jane@firm.com"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
            Company *
          </label>
          <input
            type="text"
            value={form.company}
            onChange={handleChange("company")}
            className={inputClass}
            placeholder="Acme Ventures"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
            Title
          </label>
          <input
            type="text"
            value={form.title}
            onChange={handleChange("title")}
            className={inputClass}
            placeholder="Partner"
          />
        </div>
      </div>

      {/* NDA text */}
      <div className="flex flex-col gap-2">
        <span className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
          Non-Disclosure Agreement
        </span>
        <div className="max-h-64 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)] p-4">
          <pre className="text-[12px] text-[var(--color-fg-secondary)] font-[family-name:var(--font-mono)] whitespace-pre-wrap leading-relaxed">
            {NDA_TEXT}
          </pre>
        </div>
      </div>

      {/* Agreement checkbox */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => {
            setAgreed(e.target.checked);
            setError("");
          }}
          className="mt-0.5 accent-[var(--color-accent)]"
        />
        <span className="text-[12px] text-[var(--color-fg-secondary)] font-[family-name:var(--font-mono)]">
          I have read and agree to the terms of this Non-Disclosure Agreement.
        </span>
      </label>

      {/* Electronic signature */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-[family-name:var(--font-mono)] tracking-wider uppercase text-[var(--color-fg-secondary)]">
          Electronic Signature (type your full name) *
        </label>
        <input
          type="text"
          value={signatureName}
          onChange={(e) => {
            setSignatureName(e.target.value);
            setError("");
          }}
          className={inputClass}
          placeholder="Jane Smith"
        />
      </div>

      {error && (
        <p className="text-[12px] text-red-500 font-[family-name:var(--font-mono)]">{error}</p>
      )}

      <button
        type="submit"
        className="w-full py-3 rounded-lg bg-[var(--color-accent)] text-white font-[family-name:var(--font-mono)] text-[12px] font-bold uppercase tracking-widest hover:opacity-90 transition-opacity"
      >
        Sign & Continue
      </button>
    </form>
  );
}
